/**
 * Meluva — comportamiento de las secciones propias del tema.
 * Sin dependencias: cada componente es un custom element autónomo.
 */

/** Carrusel con flechas: desplaza la pista una "página" visible cada vez. */
class MeluvaCarrusel extends HTMLElement {
  connectedCallback() {
    this.pista = this.querySelector('[data-pista]');
    this.anterior = this.querySelector('[data-anterior]');
    this.siguiente = this.querySelector('[data-siguiente]');
    if (!this.pista) return;

    this.anterior?.addEventListener('click', () => this.mover(-1));
    this.siguiente?.addEventListener('click', () => this.mover(1));
    this.pista.addEventListener('scroll', () => this.actualizar(), { passive: true });
    window.addEventListener('resize', () => this.actualizar());
    this.actualizar();
  }

  mover(direccion) {
    this.pista.scrollBy({ left: direccion * this.pista.clientWidth, behavior: 'smooth' });
  }

  actualizar() {
    const { scrollLeft, scrollWidth, clientWidth } = this.pista;
    const sinScroll = scrollWidth <= clientWidth + 1;
    if (this.anterior) this.anterior.disabled = sinScroll || scrollLeft <= 1;
    if (this.siguiente) this.siguiente.disabled = sinScroll || scrollLeft + clientWidth >= scrollWidth - 1;
  }
}

/** Ficha de producto: variantes en píldoras, galería y selector de cantidad. */
class MeluvaProducto extends HTMLElement {
  connectedCallback() {
    const datos = this.querySelector('[data-variantes]');
    this.variantes = datos ? JSON.parse(datos.textContent) : [];
    this.inputVariante = this.querySelector('[data-variante-id]');
    this.precio = this.querySelector('[data-precio]');
    this.stock = this.querySelector('[data-stock]');
    this.botones = this.querySelectorAll('[data-boton-compra]');

    this.querySelectorAll('input[data-posicion]').forEach((input) => {
      input.addEventListener('change', () => this.cambiarVariante());
    });

    this.querySelectorAll('.meluva-galeria__miniatura').forEach((boton) => {
      boton.addEventListener('click', () => this.mostrarMedia(boton.dataset.mediaId));
    });

    this.querySelectorAll('[data-cantidad]').forEach((boton) => {
      boton.addEventListener('click', () => {
        const input = boton.parentElement.querySelector('input');
        const valor = Math.max(1, (parseInt(input.value, 10) || 1) + Number(boton.dataset.cantidad));
        input.value = valor;
      });
    });
  }

  cambiarVariante() {
    const seleccion = [...this.querySelectorAll('input[data-posicion]:checked')]
      .sort((a, b) => a.dataset.posicion - b.dataset.posicion)
      .map((input) => input.value);
    const variante = this.variantes.find((v) => v.options.every((opcion, i) => opcion === seleccion[i]));

    this.botones.forEach((boton) => {
      boton.disabled = !variante || !variante.available;
    });
    const comprar = this.querySelector('.meluva-ficha__comprar');
    if (comprar) comprar.textContent = variante && variante.available ? 'Comprar ya' : variante ? 'Agotado' : 'No disponible';
    if (!variante) return;

    this.inputVariante.value = variante.id;
    this.precio.innerHTML = variante.compare
      ? `${variante.price}<s class="meluva-ficha__precio-antes">${variante.compare}</s>`
      : variante.price;
    if (this.stock) {
      this.stock.lastChild.textContent = variante.available ? 'En stock' : 'Sin stock';
    }
    if (variante.media) this.mostrarMedia(String(variante.media));

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variante.id);
    window.history.replaceState({}, '', url);
  }

  mostrarMedia(id) {
    this.querySelectorAll('.meluva-galeria__slide[data-media-id]').forEach((slide) => {
      slide.hidden = slide.dataset.mediaId !== id;
    });
    this.querySelectorAll('.meluva-galeria__miniatura').forEach((boton) => {
      if (boton.dataset.mediaId === id) boton.setAttribute('aria-current', 'true');
      else boton.removeAttribute('aria-current');
    });
  }
}

/** Código descuento en la cesta: Shopify lo aplica con /discount/CODIGO y vuelve a la cesta. */
class MeluvaDescuento extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-descuento]')?.addEventListener('submit', (evento) => {
      evento.preventDefault();
      const codigo = new FormData(evento.target).get('codigo').trim();
      if (!codigo) return;
      window.location.href = `/discount/${encodeURIComponent(codigo)}?redirect=${encodeURIComponent('/cart')}`;
    });
  }
}

/**
 * Logo animado de la cabecera (Claude Design: "Meluva Animacion"), en bucle de 6,4 s:
 *   Drop  (0–1,4 s)  el punto rosa cae y rebota sobre la "m"
 *   Roll  (1,4–4,6)  salta letra a letra, cada letra brota a su paso y el logo se sostiene
 *   Outro (4,6–6,4)  las letras caen, el punto salta al centro, crece y desaparece
 * Al terminar el Outro vuelve a caer el punto, así que el ciclo no tiene cortes.
 * Las distancias del original están a 300 px de cuerpo y se escalan al tamaño del logo.
 * Con movimiento reducido el logo se queda quieto.
 */
const MeluvaEasing = {
  easeInQuad: (t) => t * t,
  easeOutCubic: (t) => (t - 1) ** 3 + 1,
  easeInOutSine: (t) => -(Math.cos(Math.PI * t) - 1) / 2,
  easeOutBack: (t) => 1 + 2.70158 * (t - 1) ** 3 + 1.70158 * (t - 1) ** 2,
  easeInBack: (t) => 2.70158 * t ** 3 - 1.70158 * t ** 2,
};

const meluvaTramo = (T, desde, hasta, inicio, fin, ease) => {
  if (T <= inicio) return desde;
  if (T >= fin) return hasta;
  return desde + (hasta - desde) * ease((T - inicio) / (fin - inicio));
};

const meluvaAplastar = (T, en, dur = 0.2) => {
  const u = (T - en) / dur;
  return u < 0 || u > 1 ? 1 : 1 - 0.22 * Math.sin(Math.PI * u);
};

/* Ciclo: el punto cae y dibuja el logo (0–LLENO), el logo se queda entero hasta OUTRO para
   que se lea y se pueda pulsar, y luego se va (OUTRO–CICLO). Mientras el ratón o el foco
   están en el logo entero, el reloj se detiene y no se va. */
class MeluvaLogo extends HTMLElement {
  static HOP = 0.3;
  static ROLL = 1.4;
  static LLENO = 3.7;
  static OUTRO = 12.5;
  static CICLO = 14.3;

  connectedCallback() {
    this.letras = [...this.querySelectorAll('.meluva-logo__letra')];
    this.punto = this.querySelector('.meluva-logo__punto');
    this.reducido = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!this.punto || this.letras.length === 0) return this.mostrar();

    this.reducido.addEventListener('change', () => this.iniciar());
    this.alMedir = () => this.medir();
    window.addEventListener('resize', this.alMedir);

    const zona = this.closest('a') || this;
    this.retenido = false;
    const retener = (si) => {
      this.retenido = si;
      if (!si) MeluvaLogo.guardarInicio(this.t0);
    };
    zona.addEventListener('pointerenter', () => retener(true));
    zona.addEventListener('pointerleave', () => retener(false));
    zona.addEventListener('focusin', () => retener(true));
    zona.addEventListener('focusout', () => retener(false));
    // Al pulsar el logo para ir al inicio, la página nueva sigue desde el mismo punto
    window.addEventListener('pagehide', () => this.t0 && MeluvaLogo.guardarInicio(this.t0));
    this.iniciar();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.cuadro);
    window.removeEventListener('resize', this.alMedir);
  }

  async iniciar() {
    cancelAnimationFrame(this.cuadro);
    if (this.reducido.matches) {
      this.limpiar();
      return this.mostrar();
    }
    if (document.fonts) await document.fonts.ready;
    this.medir();
    this.t0 = MeluvaLogo.inicioSesion();
    const fase = (ahora) => ((performance.timeOrigin + ahora - this.t0) / 1000) % MeluvaLogo.CICLO;
    let anterior = performance.now();
    this.dibujar(fase(anterior));
    this.mostrar();

    const paso = (ahora) => {
      // Retenido con el logo entero: el inicio avanza con el reloj y la fase no cambia
      const T = fase(ahora);
      if (this.retenido && T >= MeluvaLogo.LLENO && T < MeluvaLogo.OUTRO) this.t0 += ahora - anterior;
      anterior = ahora;
      this.dibujar(fase(ahora));
      this.cuadro = requestAnimationFrame(paso);
    };
    this.cuadro = requestAnimationFrame(paso);
  }

  static guardarInicio(t0) {
    try {
      sessionStorage.setItem('meluva-logo-inicio', String(t0));
    } catch (error) {}
  }

  /* El bucle cuenta desde la primera página de la visita, así que al cambiar de página
     el logo sigue por donde iba en vez de volver a empezar. */
  static inicioSesion() {
    const ahora = performance.timeOrigin + performance.now();
    try {
      const guardado = Number(sessionStorage.getItem('meluva-logo-inicio'));
      if (guardado && guardado <= ahora) return guardado;
      sessionStorage.setItem('meluva-logo-inicio', String(ahora));
    } catch (error) {}
    return ahora;
  }

  /* offsetLeft ignora las transformaciones, así que se puede medir en plena animación */
  medir() {
    const centro = (el) => el.offsetLeft + el.offsetWidth / 2;
    this.escala = parseFloat(getComputedStyle(this).fontSize) / 300;
    this.finalX = centro(this.punto);
    this.mitad = this.offsetWidth / 2;
    this.puntos = [...this.letras.map(centro), this.finalX];
  }

  mostrar() {
    document.documentElement.classList.remove('meluva-logo-pendiente');
  }

  dibujar(T) {
    const { HOP, ROLL, OUTRO } = MeluvaLogo;
    const pts = this.puntos;
    const saltos = ROLL + 0.15;

    // Punto — Drop: cae y rebota sobre la "m"
    let dx = pts[0];
    let dy = meluvaTramo(T, -900, 0, 0.2, 0.8, MeluvaEasing.easeInQuad);
    if (T > 0.8) {
      const u = Math.min((T - 0.8) / 0.45, 1);
      dy = -4 * 110 * u * (1 - u);
    }
    // Roll: salta de letra en letra hasta su sitio
    const k = (T - saltos) / HOP;
    if (k > 0) {
      const i = Math.min(Math.floor(k), pts.length - 2);
      const u = k >= pts.length - 1 ? 1 : k - i;
      dx = pts[i] + (pts[i + 1] - pts[i]) * MeluvaEasing.easeInOutSine(u);
      dy = -4 * 230 * u * (1 - u) * (i === pts.length - 2 ? 0.8 : 1);
    }
    // Outro: salta al centro, crece y desaparece
    if (T > OUTRO + 0.45) {
      const u = Math.min((T - (OUTRO + 0.45)) / 0.5, 1);
      dx = this.finalX + (this.mitad - this.finalX) * MeluvaEasing.easeInOutSine(u);
      dy = -4 * 260 * u * (1 - u);
    }
    let tamano = meluvaTramo(T, 1, 1.35, OUTRO + 1.0, OUTRO + 1.25, MeluvaEasing.easeOutCubic);
    if (T > OUTRO + 1.25) tamano = meluvaTramo(T, 1.35, 0, OUTRO + 1.25, OUTRO + 1.55, MeluvaEasing.easeInBack);

    const impactos = [0.8, 1.25, ...pts.slice(1).map((_, i) => saltos + (i + 1) * HOP), OUTRO + 0.95];
    const sx = impactos.reduce((a, en) => a * meluvaAplastar(T, en), 1);
    this.punto.style.transform =
      `translate(${dx - this.finalX}px, ${dy * this.escala}px) ` +
      `scale(${Math.max(tamano * (2 - sx), 0)}, ${Math.max(tamano * sx, 0)})`;

    // Letras: brotan al paso del punto y caen en el Outro
    this.letras.forEach((letra, i) => {
      const en = saltos + i * HOP;
      const p = meluvaTramo(T, 0, 1, en, en + 0.42, MeluvaEasing.easeOutBack);
      const cae = OUTRO + 0.05 + i * 0.05;
      const fuera = meluvaTramo(T, 0, 1, cae, cae + 0.4, MeluvaEasing.easeInBack);
      letra.style.opacity = T < en ? '0' : String(Math.min(Math.max(1 - fuera, 0), 1));
      letra.style.transform = `translateY(${(1 - p) * 0.25 + fuera * 0.9}em) scale(${Math.max(p, 0)})`;
    });
  }

  limpiar() {
    [...this.letras, this.punto].forEach((el) => {
      el.style.removeProperty('transform');
      el.style.removeProperty('opacity');
    });
  }
}

/**
 * Catálogo (Claude Design: "Catalogo Meluva"). Dibuja las vistas Estantes y Mosaico
 * a partir del JSON de sections/meluva-catalogo.liquid. Al cambiar de vista,
 * categoría, etiqueta, orden o búsqueda, cada tarjeta viaja de su posición anterior a
 * la nueva (FLIP); las que aparecen entran desde abajo. La vista rápida añade a la
 * cesta con la Cart API sin salir de la página.
 */
const meluvaEscapar = (texto) =>
  String(texto ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const meluvaRuta = (ruta) => `${window.Shopify?.routes?.root || '/'}${ruta}`;

/** Número de productos en el botón de la cesta de la cabecera (sin número si está vacía). */
function meluvaPintarContador(total) {
  document.querySelectorAll('.meluva-boton-cesta').forEach((boton) => {
    let contador = boton.querySelector('.meluva-contador');
    if (!total) {
      contador?.remove();
    } else {
      if (!contador) {
        contador = document.createElement('span');
        contador.className = 'meluva-contador';
        contador.setAttribute('aria-hidden', 'true');
        boton.append(contador);
      }
      contador.textContent = total;
    }
    boton.setAttribute('aria-label', `Cesta, ${total} ${total === 1 ? 'producto' : 'productos'}`);
  });
}

/**
 * Añade una variante a la cesta, actualiza el contador y abre la cesta lateral con el contenido
 * nuevo (la respuesta de la Cart API ya trae la sección pintada). Sin cesta lateral (página de
 * la cesta), solo actualiza el contador.
 */
async function meluvaAnadirALaCesta(id, cantidad = 1) {
  const cestaLateral = document.querySelector('meluva-cesta-lateral');
  const respuesta = await fetch(meluvaRuta('cart/add.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: [{ id, quantity: cantidad }],
      ...(cestaLateral && { sections: [MeluvaCestaLateral.SECCION], sections_url: window.location.pathname }),
    }),
  });
  const datos = await respuesta.json().catch(() => ({}));
  if (!respuesta.ok) throw new Error(datos.description || 'No se pudo añadir');

  if (cestaLateral) {
    await cestaLateral.pintar(datos.sections?.[MeluvaCestaLateral.SECCION]);
    cestaLateral.abrir();
  } else {
    const cesta = await fetch(meluvaRuta('cart.js')).then((r) => r.json());
    meluvaPintarContador(cesta.item_count);
  }
}

/**
 * Cesta lateral (sections/meluva-cesta-lateral.liquid). Se abre desde la derecha con la cesta de
 * la cabecera o al añadir un producto: una cinta rosa barre de derecha a izquierda y detrás entra
 * el panel crema, como el menú móvil pero al revés. Cantidades y "Eliminar" van por la Cart API,
 * que devuelve la sección ya pintada; solo se sustituye [data-cesta-cuerpo].
 */
class MeluvaCestaLateral extends HTMLElement {
  static SECCION = 'meluva-cesta-lateral';

  connectedCallback() {
    this.panel = this.querySelector('.meluva-cesta-lateral__panel');
    this.sinMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('click', (evento) => {
      if (evento.target.closest('[data-cerrar-cesta]')) {
        this.cerrar();
        return;
      }
      const boton = evento.target.closest('[data-linea]');
      if (boton) this.cambiar(boton);
    });
    this.addEventListener('keydown', (evento) => this.teclado(evento));
    // Al volver con "atrás" el navegador puede enseñar la página guardada con una cesta antigua
    window.addEventListener('pageshow', (evento) => {
      if (evento.persisted) this.refrescar();
    });
  }

  get abierta() {
    return !this.hidden && !this.classList.contains('is-cerrando');
  }

  abrir(origen) {
    if (this.abierta) return;
    this.origen = origen || document.activeElement;
    clearTimeout(this.respaldo);
    this.classList.remove('is-cerrando');
    // Las entradas escalonadas solo al abrir, no cada vez que cambia una cantidad
    this.classList.add('is-entrando');
    clearTimeout(this.finEntrada);
    this.finEntrada = setTimeout(() => this.classList.remove('is-entrando'), 1400);
    this.hidden = false;
    document.documentElement.classList.add('meluva-sin-scroll');
    this.panel.focus({ preventScroll: true });
  }

  cerrar() {
    if (!this.abierta) return;
    const terminar = () => {
      clearTimeout(this.respaldo);
      this.classList.remove('is-cerrando', 'is-entrando');
      this.hidden = true;
      document.documentElement.classList.remove('meluva-sin-scroll');
      this.origen?.focus?.({ preventScroll: true });
    };
    if (this.sinMovimiento.matches) {
      terminar();
      return;
    }
    this.classList.add('is-cerrando');
    const panel = this.panel;
    panel.addEventListener('animationend', function alTerminar(evento) {
      if (evento.target !== panel) return;
      panel.removeEventListener('animationend', alTerminar);
      terminar();
    });
    // Por si el navegador no llega a lanzar animationend
    this.respaldo = setTimeout(terminar, 600);
  }

  teclado(evento) {
    if (evento.key === 'Escape') {
      evento.preventDefault();
      this.cerrar();
      return;
    }
    if (evento.key !== 'Tab') return;
    const enfocables = [...this.panel.querySelectorAll('a[href], button:not([disabled])')].filter(
      (el) => el.offsetParent !== null
    );
    if (!enfocables.length) return;
    const primero = enfocables[0];
    const ultimo = enfocables[enfocables.length - 1];
    if (evento.shiftKey && (document.activeElement === primero || document.activeElement === this.panel)) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!evento.shiftKey && document.activeElement === ultimo) {
      evento.preventDefault();
      primero.focus();
    }
  }

  /** Cambia la cantidad de una línea (0 la elimina) y vuelve a pintar la cesta. */
  async cambiar(boton) {
    if (this.ocupada) return;
    this.ocupada = true;
    this.panel.setAttribute('aria-busy', 'true');
    const linea = boton.dataset.linea;
    const hermanos = [...this.querySelectorAll(`[data-linea="${CSS.escape(linea)}"]`)];
    const posicion = hermanos.indexOf(boton);
    try {
      const respuesta = await fetch(meluvaRuta('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: linea,
          quantity: Number(boton.dataset.cantidad),
          sections: [MeluvaCestaLateral.SECCION],
          sections_url: window.location.pathname,
        }),
      });
      const datos = await respuesta.json().catch(() => ({}));
      await this.pintar(respuesta.ok ? datos.sections?.[MeluvaCestaLateral.SECCION] : null);
    } finally {
      this.ocupada = false;
      this.panel.removeAttribute('aria-busy');
      // El foco vuelve al mismo botón de la línea; si la línea ya no está, al panel
      const mismo = this.querySelectorAll(`[data-linea="${CSS.escape(linea)}"]`)[posicion];
      (mismo || this.panel).focus({ preventScroll: true });
    }
  }

  /** Sustituye el contenido por el HTML de la sección (o lo pide si no viene). */
  async pintar(html) {
    if (!html) {
      await this.refrescar();
      return;
    }
    const nuevo = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cesta-cuerpo]');
    if (!nuevo) return;
    this.querySelector('[data-cesta-cuerpo]').replaceWith(nuevo);
    meluvaPintarContador(Number(nuevo.dataset.total) || 0);
  }

  async refrescar() {
    try {
      const url = `${window.location.pathname}?sections=${MeluvaCestaLateral.SECCION}`;
      const datos = await fetch(url).then((r) => r.json());
      if (datos[MeluvaCestaLateral.SECCION]) await this.pintar(datos[MeluvaCestaLateral.SECCION]);
    } catch (error) {}
  }
}

class MeluvaCatalogo extends HTMLElement {
  static VISTAS = ['shelves', 'mosaic'];
  static ORDENES = [
    ['destacados', 'Destacados'],
    ['asc', 'Precio ↑'],
    ['desc', 'Precio ↓'],
  ];

  connectedCallback() {
    const datos = JSON.parse(this.querySelector('[data-catalogo]').textContent);
    this.subtitulo = datos.subtitulo || '';
    this.fotos = datos.fotos || {};
    this.sinMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.prepararCategorias(datos);

    let vista = this.dataset.vista;
    try {
      vista = localStorage.getItem('meluva-catalogo-vista') || vista;
    } catch (error) {}
    const parametros = new URLSearchParams(window.location.search);
    const categoriaUrl = (parametros.get('categoria') || '').trim().toLowerCase();
    this.estado = {
      vista: MeluvaCatalogo.VISTAS.includes(vista) ? vista : 'shelves',
      cat: this.categorias.some((c) => c.id === categoriaUrl) ? categoriaUrl : 'all',
      ofertas: parametros.has('ofertas'),
      etiqueta: null,
      orden: 0,
      busqueda: '',
    };

    this.resultados = this.querySelector('[data-resultados]');
    this.rapida = this.querySelector('[data-rapida]');
    this.velo = this.querySelector('[data-velo]');
    this.querySelector('[data-vistas]').hidden = false;
    this.querySelector('[data-barra]').hidden = false;

    this.addEventListener('click', (evento) => this.alHacerClic(evento));
    this.velo.addEventListener('click', () => this.cerrarRapida());
    this.conectarBuscador();
    this.conectarCabecera();

    this.classList.add('is-intro');
    this.pintar();
    this.introTiempo = setTimeout(() => this.classList.remove('is-intro'), 1400);
  }

  disconnectedCallback() {
    clearTimeout(this.introTiempo);
  }

  /* Categorías: primero las de los bloques (en su orden), luego otros tipos que
     aparezcan en los productos y, al final, "Otros" para los que no tienen tipo. */
  prepararCategorias(datos) {
    const clave = (tipo) => (tipo || '').trim().toLowerCase();
    const tipos = new Map();
    datos.categorias.forEach((c) => {
      if (clave(c.tipo) && !tipos.has(clave(c.tipo))) tipos.set(clave(c.tipo), { nombre: c.nombre || c.tipo, texto: c.texto || '' });
    });
    datos.productos.forEach((p) => {
      if (clave(p.tipo) && !tipos.has(clave(p.tipo))) tipos.set(clave(p.tipo), { nombre: p.tipo, texto: '' });
    });

    this.productos = datos.productos.map((p, orden) => ({ ...p, orden, cat: clave(p.tipo) || '_otros' }));
    const categorias = [...tipos].map(([id, c]) => ({ id, ...c }));
    const sinTipo = this.productos.some((p) => p.cat === '_otros');
    if (sinTipo) {
      categorias.push(
        categorias.some((c) => this.productos.some((p) => p.cat === c.id))
          ? { id: '_otros', nombre: 'Otros', texto: '' }
          : { id: '_otros', nombre: 'Todo', texto: this.subtitulo }
      );
    }
    this.categorias = categorias
      .map((c) => ({ ...c, productos: this.productos.filter((p) => p.cat === c.id) }))
      .filter((c) => c.productos.length);
    this.etiquetas = [...new Set(this.productos.flatMap((p) => p.etiquetas))];
  }

  conectarBuscador() {
    document.querySelectorAll('.meluva-buscador').forEach((formulario) => {
      const campo = formulario.querySelector('input[type="search"]');
      if (!campo) return;
      campo.addEventListener('input', () => this.cambiar({ busqueda: campo.value }));
      formulario.addEventListener('submit', (evento) => {
        evento.preventDefault();
        this.cambiar({ busqueda: campo.value });
        this.scrollIntoView({ behavior: this.sinMovimiento.matches ? 'auto' : 'smooth', block: 'start' });
      });
    });
  }

  nombreCategoria(id) {
    return this.categorias.find((c) => c.id === id)?.nombre || '';
  }

  /** Productos visibles según categoría, etiqueta, búsqueda y orden. */
  filtrados() {
    const { cat, etiqueta, busqueda, orden, ofertas } = this.estado;
    const criterio = MeluvaCatalogo.ORDENES[orden][0];
    let lista = this.productos.filter(
      (p) => (cat === 'all' || p.cat === cat) && (!ofertas || p.oferta) && (!etiqueta || p.etiquetas.includes(etiqueta))
    );

    // La búsqueda de la cabecera usa el mismo criterio que sus sugerencias (errores, tildes, español)
    if (busqueda.trim()) {
      const palabrasCon = meluvaPalabras(busqueda);
      const puntos = new Map(
        lista.map((p) => [p, meluvaPuntuar(palabrasCon, [[p.titulo, 3], [this.nombreCategoria(p.cat), 2], [p.etiquetas.join(' '), 1.5]])])
      );
      const maximo = Math.max(0, ...puntos.values());
      lista = lista.filter((p) => puntos.get(p) > 0.5 && puntos.get(p) >= maximo * 0.35);
    }

    return lista.sort((a, b) => (criterio === 'asc' ? a.precio - b.precio : criterio === 'desc' ? b.precio - a.precio : a.orden - b.orden));
  }

  cambiar(cambios) {
    if (Object.keys(cambios).every((k) => this.estado[k] === cambios[k])) return;
    const antes = this.medir();
    Object.assign(this.estado, cambios);
    if ('vista' in cambios) {
      try {
        localStorage.setItem('meluva-catalogo-vista', cambios.vista);
      } catch (error) {}
    }
    this.pintar();
    this.animarCambio(antes);
  }

  /* ---------- Pintado ---------- */

  pintar() {
    const { vista, cat, etiqueta, orden } = this.estado;
    const lista = this.filtrados();

    this.querySelectorAll('[data-vistas] [data-vista]').forEach((boton) => {
      boton.setAttribute('aria-pressed', String(boton.dataset.vista === vista));
    });

    const categoria = this.categorias.find((c) => c.id === cat);
    this.querySelector('[data-subtitulo]').textContent = (categoria && categoria.texto) || this.subtitulo;

    const chips = this.categorias.length > 1 ? [{ id: 'all', nombre: 'Todo', productos: this.productos }, ...this.categorias] : [];
    this.querySelector('[data-categorias]').innerHTML = chips
      .map((c) => {
        // Foto de la categoría (la de la portada), nunca la de un producto; sin foto, el punto rosa
        const imagen = this.fotos[c.id];
        return `<button type="button" class="meluva-catalogo__categoria" data-cat="${meluvaEscapar(c.id)}" aria-pressed="${c.id === cat}">
          <span class="meluva-catalogo__categoria-imagen">${
            imagen ? `<img src="${meluvaEscapar(imagen)}" alt="" width="1024" height="1024">` : '<span class="meluva-punto" aria-hidden="true"></span>'
          }</span>
          ${meluvaEscapar(c.nombre)}
        </button>`;
      })
      .join('');

    const chipOfertas = this.estado.ofertas
      ? '<button type="button" class="meluva-catalogo__etiqueta" data-quitar-ofertas aria-pressed="true">Ofertas ✕</button>'
      : '';
    this.querySelector('[data-etiquetas]').innerHTML = chipOfertas + this.etiquetas
      .map(
        (t) =>
          `<button type="button" class="meluva-catalogo__etiqueta" data-etiqueta="${meluvaEscapar(t)}" aria-pressed="${t === etiqueta}">${meluvaEscapar(t)}</button>`
      )
      .join('');
    this.querySelector('[data-contador]').textContent = lista.length === 1 ? '1 producto' : `${lista.length} productos`;
    this.querySelector('[data-orden]').textContent = `Ordenar: ${MeluvaCatalogo.ORDENES[orden][1]} ▾`;

    if (!lista.length) {
      this.resultados.innerHTML = `<p class="meluva-catalogo__vacio">${
        this.estado.ofertas ? 'Ahora mismo no hay ofertas. Vuelve pronto.' : 'Nada por aquí. Prueba con otra categoría o búsqueda.'
      }</p>`;
    } else if (vista === 'mosaic') {
      this.resultados.innerHTML = this.pintarMosaico(lista);
    } else {
      this.resultados.innerHTML = this.pintarEstantes(lista);
    }
    this.resultados.dataset.vista = vista;
    this.marcarCabecera();
  }

  /** El punto rosa de la cabecera y el panel móvil siguen a la categoría elegida. */
  marcarCabecera() {
    const { cat, ofertas } = this.estado;
    document.querySelectorAll('.meluva-cabecera [data-categoria]').forEach((enlace) => {
      const activo = !ofertas && enlace.dataset.categoria === cat;
      enlace.classList.toggle('is-activo', activo);
      if (activo) enlace.setAttribute('aria-current', 'true');
      else enlace.removeAttribute('aria-current');
    });
  }

  /* En el catálogo, las categorías y Ofertas de la cabecera filtran sin recargar la página. */
  conectarCabecera() {
    document.addEventListener('click', (evento) => {
      if (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.button !== 0) return;
      const enlace = evento.target.closest('.meluva-cabecera [data-categoria], .meluva-cabecera .meluva-ofertas');
      if (!enlace) return;
      const cat = enlace.dataset.categoria;
      if (cat && cat !== 'all' && !this.categorias.some((c) => c.id === cat)) return;
      evento.preventDefault();

      const menu = enlace.closest('.meluva-menu-movil');
      if (menu) cerrarMenuMovil(menu);
      this.cambiar(cat ? { cat, ofertas: false } : { cat: 'all', ofertas: true });
      this.actualizarUrl();
      window.scrollTo({ top: 0, behavior: this.sinMovimiento.matches ? 'auto' : 'smooth' });
    });
  }

  actualizarUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('categoria');
    url.searchParams.delete('ofertas');
    if (this.estado.ofertas) url.searchParams.set('ofertas', '1');
    else if (this.estado.cat !== 'all') url.searchParams.set('categoria', this.estado.cat);
    window.history.replaceState(window.history.state, '', url);
  }

  distintivo(p, clase) {
    return p.distintivo ? `<span class="${clase}">${meluvaEscapar(p.distintivo)}</span>` : '';
  }

  imagen(p) {
    return p.imagen
      ? `<img src="${meluvaEscapar(p.imagen)}" alt="" loading="lazy">`
      : '<span class="meluva-punto meluva-punto--grande" aria-hidden="true"></span>';
  }

  botonAnadir(p, texto) {
    const etiqueta = p.unica && p.disponible ? `Añadir ${p.titulo} a la cesta` : `Elegir opciones de ${p.titulo}`;
    return `<button type="button" class="meluva-cat-anadir" data-anadir="${p.id}" aria-label="${meluvaEscapar(etiqueta)}"${
      p.disponible ? '' : ' disabled'
    }>${texto ? `<span class="meluva-cat-anadir__texto">${texto}</span>` : ''}<span class="meluva-cat-anadir__mas" aria-hidden="true">+</span></button>`;
  }

  pintarEstantes(lista) {
    return this.categorias
      .map((c, posicion) => ({ ...c, numero: String(posicion + 1).padStart(2, '0'), items: lista.filter((p) => p.cat === c.id) }))
      .filter((c) => c.items.length)
      .map((c, i) => {
        const tarjetas = c.items
          .map(
            (p, j) => `<article class="meluva-cat-tarjeta" data-flip="p${p.id}" style="--retraso:${i * 110 + 120 + j * 70}ms">
              <a href="${meluvaEscapar(p.url)}" class="meluva-cat-tarjeta__enlace" data-abrir="${p.id}">
                <span class="meluva-cat-tarjeta__imagen">${this.imagen(p)}${this.distintivo(p, 'meluva-cat-distintivo')}</span>
                <span class="meluva-cat-tarjeta__textos">
                  <span class="meluva-cat-tarjeta__nombre">${meluvaEscapar(p.titulo)}</span>
                  <span class="meluva-cat-tarjeta__precio">${meluvaEscapar(p.precioTexto)}</span>
                </span>
              </a>
              ${this.botonAnadir(p)}
            </article>`
          )
          .join('');
        return `<section class="meluva-cat-estante" style="--retraso:${i * 110}ms" aria-label="${meluvaEscapar(c.nombre)}">
          <div class="meluva-cat-estante__cabeza" data-flip="s${meluvaEscapar(c.id)}">
            <div class="meluva-cat-estante__numero"><span>${c.numero}</span><span class="meluva-punto" aria-hidden="true"></span></div>
            <div class="meluva-cat-estante__textos">
              <h2>${meluvaEscapar(c.nombre)}</h2>
              ${c.texto ? `<p>${meluvaEscapar(c.texto)}</p>` : ''}
            </div>
            ${
              this.categorias.length > 1 && this.estado.cat !== c.id
                ? `<button type="button" class="meluva-cat-estante__ver" data-cat="${meluvaEscapar(c.id)}">Ver ${meluvaEscapar(c.nombre.toLowerCase())} →</button>`
                : ''
            }
          </div>
          <div class="meluva-cat-estante__riel" data-riel>${tarjetas}</div>
        </section>`;
      })
      .join('');
  }

  pintarMosaico(lista) {
    return `<div class="meluva-cat-mosaico">${lista
      .map((p, i) => {
        const grande = i === 0 && lista.length > 2;
        return `<article class="meluva-cat-pieza${grande ? ' meluva-cat-pieza--grande' : ''}" data-flip="p${p.id}" style="--retraso:${i * 70}ms">
          <a href="${meluvaEscapar(p.url)}" class="meluva-cat-pieza__enlace" data-abrir="${p.id}">
            <span class="meluva-cat-pieza__arriba">
              <span class="meluva-cat-pastilla">${meluvaEscapar(this.nombreCategoria(p.cat))}</span>
              ${p.distintivo ? `<span class="meluva-cat-pieza__distintivo"><span class="meluva-punto" aria-hidden="true"></span>${meluvaEscapar(p.distintivo)}</span>` : ''}
            </span>
            <span class="meluva-cat-pieza__imagen">${this.imagen(p)}</span>
            <span class="meluva-cat-pieza__textos">
              <span class="meluva-cat-pieza__nombre">${meluvaEscapar(p.titulo)}</span>
              <span class="meluva-cat-pieza__precio">${meluvaEscapar(p.precioTexto)}</span>
            </span>
          </a>
          ${this.botonAnadir(p, grande ? 'Añadir' : '')}
        </article>`;
      })
      .join('')}</div>`;
  }

  /* ---------- Interacción ---------- */

  alHacerClic(evento) {
    const objetivo = evento.target.closest(
      '[data-quitar-ofertas], [data-cat], [data-etiqueta], [data-vista], [data-orden], [data-anadir], [data-abrir], [data-cerrar], [data-variante]'
    );
    if (!objetivo || !this.contains(objetivo)) return;
    const { dataset } = objetivo;

    if ('quitarOfertas' in dataset) {
      this.cambiar({ ofertas: false });
      this.actualizarUrl();
    } else if ('cat' in dataset) {
      this.cambiar({ cat: dataset.cat, ofertas: false });
      this.actualizarUrl();
    }
    else if ('etiqueta' in dataset) this.cambiar({ etiqueta: this.estado.etiqueta === dataset.etiqueta ? null : dataset.etiqueta });
    else if ('vista' in dataset && objetivo.closest('[data-vistas]')) this.cambiar({ vista: dataset.vista });
    else if ('orden' in dataset) this.cambiar({ orden: (this.estado.orden + 1) % MeluvaCatalogo.ORDENES.length });
    else if ('cerrar' in dataset) this.cerrarRapida();
    else if ('variante' in dataset) this.elegirVariante(Number(dataset.variante));
    else if ('anadir' in dataset) this.anadir(Number(dataset.anadir), objetivo);
    else if ('abrir' in dataset) {
      // Con Ctrl/Cmd/Mayús o botón central el enlace abre la ficha como siempre
      if (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.button !== 0) return;
      evento.preventDefault();
      this.abrirRapida(Number(dataset.abrir), objetivo);
    }
  }

  async anadir(id, boton) {
    const p = this.productos.find((x) => x.id === id);
    if (!p) return;
    const enRapida = this.rapida.contains(boton);
    if (!enRapida && !(p.unica && p.disponible)) {
      this.abrirRapida(id, boton);
      return;
    }
    const variante = enRapida ? this.varianteElegida : p.variantes[0];
    if (!variante || !variante.disponible || boton.disabled) return;

    boton.disabled = true;
    boton.classList.add('is-cargando');
    const textoOriginal = boton.innerHTML;
    try {
      await meluvaAnadirALaCesta(variante.id);
      boton.classList.add('is-anadido');
      if (enRapida) boton.textContent = 'Añadido ✓';
    } catch (error) {
      boton.classList.add('is-error');
      if (enRapida) boton.textContent = error.message;
    } finally {
      boton.classList.remove('is-cargando');
      setTimeout(() => {
        boton.classList.remove('is-anadido', 'is-error');
        boton.innerHTML = textoOriginal;
        boton.disabled = false;
      }, 1600);
    }
  }

  /* ---------- Vista rápida ---------- */

  abrirRapida(id, origen) {
    const p = this.productos.find((x) => x.id === id);
    if (!p) return;
    this.origenRapida = origen;
    this.productoRapida = p;

    this.rapida.querySelector('[data-rapida-categoria]').textContent = this.nombreCategoria(p.cat);
    this.rapida.querySelector('[data-rapida-imagen]').innerHTML = this.imagen(p);
    this.rapida.querySelector('[data-rapida-nombre]').textContent = p.titulo;
    const descripcion = this.rapida.querySelector('[data-rapida-descripcion]');
    descripcion.textContent = p.descripcion;
    descripcion.hidden = !p.descripcion;
    this.rapida.querySelector('[data-rapida-ficha]').href = p.url;
    this.rapida.querySelector('[data-rapida-etiquetas]').innerHTML = p.etiquetas
      .map((t) => `<span class="meluva-rapida__etiqueta">${meluvaEscapar(t)}</span>`)
      .join('');

    const variantes = this.rapida.querySelector('[data-rapida-variantes]');
    variantes.hidden = p.unica;
    variantes.innerHTML = p.unica
      ? ''
      : p.variantes
          .map(
            (v) =>
              `<button type="button" role="radio" class="meluva-rapida__variante" data-variante="${v.id}"${
                v.disponible ? '' : ' disabled'
              }>${meluvaEscapar(v.titulo)}</button>`
          )
          .join('');
    this.elegirVariante((p.variantes.find((v) => v.disponible) || p.variantes[0])?.id);

    document.documentElement.classList.add('meluva-sin-scroll');
    this.velo.hidden = false;
    this.rapida.hidden = false;
    this.rapida.classList.remove('is-cerrando');
    this.velo.classList.remove('is-cerrando');
    this.rapida.querySelector('[data-cerrar]').focus({ preventScroll: true });
    this.tecladoRapida = (e) => {
      if (e.key === 'Escape') return this.cerrarRapida();
      if (e.key !== 'Tab') return;
      // El foco se queda dentro del panel mientras está abierto
      const enfocables = [...this.rapida.querySelectorAll('a[href], button:not([disabled])')].filter((el) => !el.closest('[hidden]'));
      const primero = enfocables[0];
      const ultimo = enfocables[enfocables.length - 1];
      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    };
    document.addEventListener('keydown', this.tecladoRapida);
  }

  elegirVariante(id) {
    const p = this.productoRapida;
    const v = p?.variantes.find((x) => x.id === id);
    if (!v) return;
    this.varianteElegida = v;
    this.rapida.querySelectorAll('[data-variante]').forEach((boton) => {
      boton.setAttribute('aria-checked', String(Number(boton.dataset.variante) === id));
    });
    this.rapida.querySelector('[data-rapida-precio]').textContent = v.precioTexto;
    const anadir = this.rapida.querySelector('[data-rapida-anadir]');
    anadir.dataset.anadir = p.id;
    anadir.disabled = !v.disponible;
    anadir.textContent = v.disponible ? 'Añadir a la cesta' : 'Agotado';
  }

  cerrarRapida() {
    if (this.rapida.hidden || this.rapida.classList.contains('is-cerrando')) return;
    document.removeEventListener('keydown', this.tecladoRapida);
    const terminar = () => {
      this.rapida.hidden = true;
      this.velo.hidden = true;
      this.rapida.classList.remove('is-cerrando');
      this.velo.classList.remove('is-cerrando');
      document.documentElement.classList.remove('meluva-sin-scroll');
    };
    if (this.origenRapida?.isConnected) this.origenRapida.focus({ preventScroll: true });
    if (this.sinMovimiento.matches) return terminar();
    this.rapida.classList.add('is-cerrando');
    this.velo.classList.add('is-cerrando');
    setTimeout(terminar, 280);
  }

  /* ---------- Animación entre estados (FLIP) ---------- */

  medir() {
    if (this.sinMovimiento.matches) return null;
    const posiciones = new Map();
    this.querySelectorAll('[data-flip]').forEach((el) => posiciones.set(el.dataset.flip, el.getBoundingClientRect()));
    return posiciones;
  }

  animarCambio(antes) {
    if (!antes) return;
    this.classList.remove('is-intro');
    const curva = 'cubic-bezier(.65,0,.2,1)';
    let movidos = 0;
    let nuevos = 0;

    // Los rieles recortan lo que sale de ellos: durante el viaje se dejan ver
    this.querySelectorAll('[data-riel]').forEach((riel) => {
      riel.classList.add('is-viajando');
      setTimeout(() => riel.classList.remove('is-viajando'), 1000);
    });

    this.querySelectorAll('[data-flip]').forEach((el) => {
      const ahora = el.getBoundingClientRect();
      const origen = antes.get(el.dataset.flip);
      if (origen && ahora.width && ahora.height) {
        const dx = origen.left + origen.width / 2 - (ahora.left + ahora.width / 2);
        const dy = origen.top + origen.height / 2 - (ahora.top + ahora.height / 2);
        const sx = origen.width / ahora.width;
        const sy = origen.height / ahora.height;
        const mismaForma = Math.abs(Math.log(sx / sy)) < 0.35;
        const escala = mismaForma ? `scale(${sx},${sy})` : `scale(${Math.min(Math.max(Math.sqrt(sx * sy), 0.3), 3)})`;
        el.animate(
          [
            { transform: `translate(${dx}px,${dy}px) ${escala}`, opacity: mismaForma ? 1 : 0.35, zIndex: 2 },
            { transform: `translate(${dx * 0.15}px,${dy * 0.15}px) scale(1.02)`, opacity: 1, offset: 0.8 },
            { transform: 'none', opacity: 1 },
          ],
          { duration: 720, delay: movidos++ * 35, easing: curva, fill: 'backwards' }
        );
      } else {
        el.animate(
          [
            { opacity: 0, transform: 'translateY(24px) scale(.94)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 520, delay: 260 + nuevos++ * 60, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'backwards' }
        );
      }
    });
  }
}

/**
 * Buscador con sugerencias (snippets/meluva-buscador.liquid).
 * - Autocompleta en gris dentro del campo el nombre de la categoría o producto más probable;
 *   Tab o → lo aceptan.
 * - Panel con las categorías y productos que más se parecen (con foto) y "Buscar «…»".
 * - Tolera errores de escritura y tildes, y entiende términos en español para productos con
 *   nombre en inglés (MELUVA_SINONIMOS).
 * El índice sale de /products.json (hasta 250 productos) y se guarda en la sesión; las
 * categorías, del menú de la cabecera.
 */
const MELUVA_SINONIMOS = {
  aceite: ['oil'], aceites: ['oil'], vitamina: ['vitamin'], polvo: ['powder'], jabon: ['soap'],
  afeitar: ['shaving', 'afeitado'], afeitado: ['shaving'], labio: ['lip', 'labios'], labios: ['lip'],
  balsamo: ['balm'], perfume: ['perfume', 'perfumes', 'edp'], colonia: ['perfume', 'edp'],
  fragancia: ['perfume', 'edp'], cuerpo: ['body'], corporal: ['body', 'cuerpo'], cara: ['facial'],
  rostro: ['facial'], canela: ['cinnamon'], ricino: ['castor'], crema: ['cream'], serum: ['serum'],
  organico: ['organic'], natural: ['natural'], pestanas: ['castor'], pelo: ['hair'], cabello: ['hair'],
};

const meluvaNormalizar = (texto) =>
  String(texto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9ñ\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Distancia de edición con transposiciones, cortando en cuanto supera el máximo. */
function meluvaDistancia(a, b, maximo) {
  if (Math.abs(a.length - b.length) > maximo) return maximo + 1;
  let previa2 = null;
  let previa = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const fila = [i];
    let minimo = i;
    for (let j = 1; j <= b.length; j++) {
      const coste = a[i - 1] === b[j - 1] ? 0 : 1;
      let valor = Math.min(previa[j] + 1, fila[j - 1] + 1, previa[j - 1] + coste);
      if (previa2 && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) valor = Math.min(valor, previa2[j - 2] + 1);
      fila.push(valor);
      minimo = Math.min(minimo, valor);
    }
    if (minimo > maximo) return maximo + 1;
    previa2 = previa;
    previa = fila;
  }
  return previa[b.length];
}

/** Parecido (0–1) entre una palabra buscada y una palabra del producto. */
function meluvaParecido(busco, palabra) {
  if (!busco || !palabra) return 0;
  if (palabra === busco) return 1;
  if (palabra.startsWith(busco)) return 0.9;
  if (busco.length >= 3 && palabra.includes(busco)) return 0.7;
  if (busco.length < 5) return 0; // con pocas letras, un error cambia la palabra entera
  const tolerancia = busco.length >= 7 ? 2 : 1;
  // Contra la palabra entera o su comienzo (para búsquedas a medio escribir)
  const inicio = palabra.slice(0, busco.length);
  const distancia = Math.min(meluvaDistancia(busco, palabra, tolerancia), meluvaDistancia(busco, inicio, tolerancia));
  return distancia <= tolerancia ? 0.6 - distancia * 0.1 : 0;
}

/** Palabras buscadas con sus equivalencias, también a medio escribir ("acei" → oil). */
function meluvaPalabras(consulta) {
  return meluvaNormalizar(consulta)
    .split(' ')
    .filter(Boolean)
    .map((palabra) => {
      const equivalentes = new Set(MELUVA_SINONIMOS[palabra] || []);
      if (palabra.length >= 3) {
        Object.entries(MELUVA_SINONIMOS).forEach(([clave, valores]) => {
          if (clave.startsWith(palabra)) valores.forEach((v) => equivalentes.add(v));
        });
      }
      return [palabra, ...equivalentes];
    });
}

/**
 * Puntuación de un texto frente a la búsqueda. campos: [[texto, peso], …].
 * Premia que coincidan todas las palabras; 0 si no coincide ninguna.
 */
function meluvaPuntuar(palabrasCon, campos) {
  let total = 0;
  let encontradas = 0;
  const palabrasCampos = campos.map(([texto, peso]) => [meluvaNormalizar(texto).split(' '), peso]);
  palabrasCon.forEach((variantes) => {
    let mejor = 0;
    palabrasCampos.forEach(([palabras, peso]) => {
      palabras.forEach((palabra) => {
        variantes.forEach((v, i) => {
          mejor = Math.max(mejor, meluvaParecido(v, palabra) * peso * (i ? 0.85 : 1));
        });
      });
    });
    if (mejor > 0) encontradas++;
    total += mejor;
  });
  return encontradas ? total * (encontradas / palabrasCon.length) ** 2 : 0;
}

class MeluvaBusqueda extends HTMLElement {
  static indice = null;

  connectedCallback() {
    this.formulario = this.querySelector('form');
    this.campo = this.querySelector('input[type="search"]');
    this.fantasma = this.querySelector('[data-fantasma]');
    this.panel = this.querySelector('.meluva-sugerencias');
    this.opciones = [];
    this.activa = -1;
    this.completar = '';
    this.moneda = this.dataset.moneda || window.Shopify?.currency?.active || 'EUR';

    this.campo.addEventListener('focus', () => {
      MeluvaBusqueda.cargarIndice();
      this.actualizar();
    });
    this.campo.addEventListener('input', () => {
      this.activa = -1;
      this.actualizar();
    });
    this.campo.addEventListener('keydown', (evento) => this.alTeclear(evento));
    this.campo.addEventListener('scroll', () => this.pintarFantasma());
    this.panel.addEventListener('mousedown', (evento) => evento.preventDefault()); // no perder el foco al pulsar
    this.panel.addEventListener('mousemove', (evento) => {
      const opcion = evento.target.closest('[role="option"]');
      if (opcion) this.marcar(this.opciones.indexOf(opcion));
    });
    this.campo.addEventListener('blur', () => setTimeout(() => this.cerrar(), 120));
    this.panel.addEventListener('click', (evento) => {
      const opcion = evento.target.closest('[role="option"]');
      if (!opcion) return;
      // En el catálogo, una categoría filtra sin recargar (MeluvaCatalogo): se limpia la búsqueda
      if (opcion.dataset.categoria && document.querySelector('meluva-catalogo')) {
        this.campo.value = '';
        this.campo.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.cerrar();
      this.campo.blur();
    });
  }

  /* ---------- Índice ---------- */

  static cargarIndice() {
    if (MeluvaBusqueda.indice) return MeluvaBusqueda.indice;
    MeluvaBusqueda.indice = (async () => {
      try {
        const guardado = JSON.parse(sessionStorage.getItem('meluva-indice') || 'null');
        if (guardado && Date.now() - guardado.fecha < 10 * 60 * 1000) return guardado.productos;
      } catch (error) {}

      const respuesta = await fetch(meluvaRuta('products.json?limit=250'));
      if (!respuesta.ok) throw new Error('Sin índice');
      const { products } = await respuesta.json();
      const productos = products.map((p) => {
        const precios = p.variants.map((v) => Number(v.price));
        return {
          titulo: p.title,
          url: meluvaRuta(`products/${p.handle}`),
          tipo: p.product_type || '',
          etiquetas: Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',').map((t) => t.trim()).filter(Boolean),
          marca: p.vendor || '',
          imagen: p.images?.[0]?.src || null,
          precio: Math.min(...precios),
          varia: new Set(precios).size > 1,
          disponible: p.variants.some((v) => v.available),
        };
      });
      try {
        sessionStorage.setItem('meluva-indice', JSON.stringify({ fecha: Date.now(), productos }));
      } catch (error) {}
      return productos;
    })().catch(() => {
      MeluvaBusqueda.indice = null;
      return [];
    });
    return MeluvaBusqueda.indice;
  }

  /** Categorías del menú de la cabecera: nombre, enlace y foto. */
  static categorias() {
    const vistas = new Map();
    document.querySelectorAll('.meluva-menu-movil__categoria[data-categoria]').forEach((enlace) => {
      const id = enlace.dataset.categoria;
      if (id === 'all' || vistas.has(id)) return;
      vistas.set(id, {
        id,
        nombre: enlace.querySelector('.meluva-menu-movil__nombre')?.textContent.trim() || id,
        url: enlace.getAttribute('href'),
        imagen: enlace.querySelector('img')?.getAttribute('src') || null,
      });
    });
    return [...vistas.values()];
  }

  /* ---------- Búsqueda ---------- */

  async actualizar() {
    const texto = this.campo.value;
    const consulta = meluvaNormalizar(texto);
    const turno = (this.turno = (this.turno || 0) + 1);
    const productos = await MeluvaBusqueda.cargarIndice();
    if (turno !== this.turno) return; // llegó otra tecla mientras tanto

    const categorias = MeluvaBusqueda.categorias();
    if (!consulta) {
      this.completar = '';
      this.pintarFantasma();
      this.pintar({ texto, categorias, productos: [], vacio: true });
      return;
    }

    const palabras = consulta.split(' ');
    const palabrasCon = meluvaPalabras(consulta);
    const puntuar = (campos) => meluvaPuntuar(palabrasCon, campos);

    const encontrados = productos
      .map((p) => {
        let puntos = puntuar([[p.titulo, 3], [p.tipo, 2], [p.etiquetas.join(' '), 1.5], [p.marca, 0.5]]);
        if (meluvaNormalizar(p.titulo).startsWith(consulta)) puntos += 3;
        if (!p.disponible) puntos *= 0.7;
        return { ...p, puntos };
      })
      .filter((p) => p.puntos > 0.5)
      .sort((a, b) => b.puntos - a.puntos)
      .filter((p, _, lista) => p.puntos >= lista[0].puntos * 0.35) // fuera los que apenas se parecen
      .slice(0, 5);

    const categoriasEncontradas = categorias
      .map((c) => ({ ...c, puntos: puntuar([[c.nombre, 3]]) + (encontrados.some((p) => meluvaNormalizar(p.tipo) === c.id) ? 0.5 : 0) }))
      .filter((c) => c.puntos > 0.5)
      .sort((a, b) => b.puntos - a.puntos)
      .slice(0, 3);

    // Autocompletado: el primer nombre que empieza exactamente por lo escrito
    const candidatos = [...categoriasEncontradas.map((c) => c.nombre), ...encontrados.map((p) => p.titulo), ...categorias.map((c) => c.nombre)];
    let completo = texto.length >= 2 && candidatos.find((nombre) => meluvaNormalizar(nombre).startsWith(consulta) && nombre.length > texto.length);
    let restante = completo ? completo.slice(texto.length) : '';
    if (!completo && texto.length >= 2 && !/\s$/.test(texto)) {
      // Si ningún nombre empieza así, completa la palabra que se está escribiendo ("vit" → "vitamin")
      const ultima = palabras[palabras.length - 1];
      const palabra = candidatos
        .flatMap((nombre) => nombre.split(/[\s()\-,]+/))
        .find((p) => p.length > ultima.length && meluvaNormalizar(p).startsWith(ultima) && meluvaNormalizar(p).length === p.length);
      if (palabra) restante = palabra.slice(ultima.length).toLowerCase();
    }
    this.completar = restante ? texto + restante : '';
    this.pintarFantasma();

    this.pintar({ texto, categorias: categoriasEncontradas, productos: encontrados, palabras: palabrasCon.flat() });
  }

  /* ---------- Pintado ---------- */

  resaltar(texto, palabras = []) {
    const limpio = meluvaEscapar(texto);
    if (!palabras.length) return limpio;
    // Marca el comienzo de cada palabra que coincide con lo buscado
    return texto
      .split(/(\s+)/)
      .map((trozo) => {
        const norma = meluvaNormalizar(trozo);
        const coincide = palabras.find((p) => p && norma.startsWith(p));
        if (!coincide) return meluvaEscapar(trozo);
        return `<mark>${meluvaEscapar(trozo.slice(0, coincide.length))}</mark>${meluvaEscapar(trozo.slice(coincide.length))}`;
      })
      .join('');
  }

  precio(p) {
    let texto;
    try {
      texto = new Intl.NumberFormat('es-ES', { style: 'currency', currency: this.moneda }).format(p.precio);
    } catch (error) {
      texto = p.precio.toFixed(2);
    }
    return p.varia ? `Desde ${texto}` : texto;
  }

  pintar({ texto, categorias, productos, palabras = [], vacio = false }) {
    const idBase = this.panel.id;
    let n = 0;
    const opcion = (url, contenido, clase, extra = '') =>
      `<a href="${meluvaEscapar(url)}" class="meluva-sugerencia ${clase}" role="option" id="${idBase}-${n++}" tabindex="-1"${extra}>${contenido}</a>`;
    const foto = (src) =>
      src
        ? `<span class="meluva-sugerencia__foto"><img src="${meluvaEscapar(src)}${src.includes('?') ? '&' : '?'}width=120" alt="" loading="lazy"></span>`
        : '<span class="meluva-sugerencia__foto"><span class="meluva-punto" aria-hidden="true"></span></span>';

    let html = '';
    if (categorias.length) {
      html += `<p class="meluva-sugerencias__titulo">${vacio ? 'Explora' : 'Categorías'}</p><div class="meluva-sugerencias__categorias">`;
      html += categorias
        .map((c) =>
          opcion(c.url, `${foto(c.imagen)}<span class="meluva-sugerencia__nombre">${this.resaltar(c.nombre, palabras)}</span>`, 'meluva-sugerencia--categoria', ` data-categoria="${meluvaEscapar(c.id)}"`)
        )
        .join('');
      html += '</div>';
    }
    if (productos.length) {
      html += '<p class="meluva-sugerencias__titulo">Productos</p>';
      html += productos
        .map((p) =>
          opcion(
            p.url,
            `${foto(p.imagen)}<span class="meluva-sugerencia__textos"><span class="meluva-sugerencia__nombre">${this.resaltar(p.titulo, palabras)}</span><span class="meluva-sugerencia__dato">${meluvaEscapar(p.tipo)}${p.disponible ? '' : ' · Agotado'}</span></span><span class="meluva-sugerencia__precio">${meluvaEscapar(this.precio(p))}</span>`,
            'meluva-sugerencia--producto'
          )
        )
        .join('');
    }
    if (!vacio) {
      if (!productos.length && !categorias.length) {
        html += '<p class="meluva-sugerencias__nada">No encontramos nada parecido. Prueba con otra palabra.</p>';
      }
      const buscar = `${this.formulario.getAttribute('action')}?q=${encodeURIComponent(texto)}&type=product`;
      html += opcion(buscar, `<span class="meluva-sugerencia__lupa" aria-hidden="true">⌕</span>Buscar «${meluvaEscapar(texto)}»`, 'meluva-sugerencia--buscar');
    }

    if (!html || document.activeElement !== this.campo) return this.cerrar();
    this.panel.innerHTML = html;
    this.opciones = [...this.panel.querySelectorAll('[role="option"]')];
    this.marcar(Math.min(this.activa, this.opciones.length - 1));
    this.abrir();
  }

  pintarFantasma() {
    const texto = this.campo.value;
    const cabe = this.campo.scrollWidth <= this.campo.clientWidth + 1;
    if (!this.completar || !cabe || this.campo.selectionStart !== texto.length) {
      this.fantasma.textContent = '';
      return;
    }
    this.fantasma.innerHTML = `<span class="meluva-buscador__escrito">${meluvaEscapar(texto)}</span>${meluvaEscapar(this.completar.slice(texto.length))}`;
  }

  abrir() {
    if (!this.panel.hidden) return;
    this.panel.hidden = false;
    this.campo.setAttribute('aria-expanded', 'true');
    this.classList.add('is-abierta');
  }

  cerrar() {
    this.panel.hidden = true;
    this.campo.setAttribute('aria-expanded', 'false');
    this.campo.removeAttribute('aria-activedescendant');
    this.classList.remove('is-abierta');
    this.activa = -1;
    this.fantasma.textContent = '';
  }

  marcar(indice) {
    this.activa = indice;
    this.opciones.forEach((o, i) => o.setAttribute('aria-selected', String(i === indice)));
    const opcion = this.opciones[indice];
    if (opcion) {
      this.campo.setAttribute('aria-activedescendant', opcion.id);
      opcion.scrollIntoView({ block: 'nearest' });
    } else {
      this.campo.removeAttribute('aria-activedescendant');
    }
  }

  aceptarFantasma() {
    if (!this.completar || this.campo.selectionStart !== this.campo.value.length) return false;
    this.campo.value = this.completar;
    this.completar = '';
    this.campo.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  alTeclear(evento) {
    const abierto = !this.panel.hidden;
    if (evento.key === 'ArrowDown' && abierto) {
      evento.preventDefault();
      this.marcar((this.activa + 1) % this.opciones.length);
    } else if (evento.key === 'ArrowUp' && abierto) {
      evento.preventDefault();
      this.marcar(this.activa <= 0 ? this.opciones.length - 1 : this.activa - 1);
    } else if ((evento.key === 'Tab' && !evento.shiftKey) || evento.key === 'ArrowRight') {
      if (this.fantasma.textContent && this.aceptarFantasma()) evento.preventDefault();
    } else if (evento.key === 'Enter' && abierto && this.opciones[this.activa]) {
      evento.preventDefault();
      evento.stopPropagation();
      this.opciones[this.activa].click();
    } else if (evento.key === 'Escape' && abierto) {
      evento.preventDefault();
      this.cerrar();
    }
  }
}

customElements.define('meluva-carrusel', MeluvaCarrusel);
customElements.define('meluva-logo', MeluvaLogo);
customElements.define('meluva-producto', MeluvaProducto);
customElements.define('meluva-descuento', MeluvaDescuento);
customElements.define('meluva-catalogo', MeluvaCatalogo);
customElements.define('meluva-busqueda', MeluvaBusqueda);
customElements.define('meluva-cesta-lateral', MeluvaCestaLateral);

/**
 * Manifiesto (sections/meluva-manifiesto.liquid). Al entrar en pantalla aparecen la etiqueta,
 * el título y el botón; al hacer scroll, la línea rosa avanza con su punto hasta la mitad de la
 * pantalla, los principios ya pasados se quedan a media luz y el que está en el centro se enciende.
 */
class MeluvaManifiesto extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.lista = this.querySelector('.meluva-manifiesto__lista');
    this.principios = [...this.querySelectorAll('.meluva-manifiesto__principio')];
    this.classList.add('is-listo');

    this.observador = new IntersectionObserver(
      ([entrada]) => {
        if (!entrada.isIntersecting) return;
        this.classList.add('is-dentro');
        this.observador.disconnect();
      },
      { threshold: 0.25 }
    );
    this.observador.observe(this);

    this.alMover = () => {
      if (this.pendiente) return;
      this.pendiente = requestAnimationFrame(() => {
        this.pendiente = null;
        this.actualizar();
      });
    };
    // Cifras: aparecen y los números enteros cuentan desde 0 cuando se ven
    const cifras = this.querySelector('.meluva-manifiesto__cifras');
    if (cifras) {
      this.observadorCifras = new IntersectionObserver(
        ([entrada]) => {
          if (!entrada.isIntersecting) return;
          this.classList.add('is-cifras');
          this.observadorCifras.disconnect();
          cifras.querySelectorAll('[data-contar]').forEach((el, i) => this.contar(el, i * 100));
        },
        { threshold: 0.4 }
      );
      this.observadorCifras.observe(cifras);
    }

    // En captura: la página no siempre se desplaza en window (Horizon puede usar .page-wrapper)
    document.addEventListener('scroll', this.alMover, { passive: true, capture: true });
    window.addEventListener('resize', this.alMover);
    this.actualizar();
  }

  disconnectedCallback() {
    this.observador?.disconnect();
    this.observadorCifras?.disconnect();
    document.removeEventListener('scroll', this.alMover, { capture: true });
    window.removeEventListener('resize', this.alMover);
  }

  contar(el, retraso) {
    const final = Number(el.dataset.contar);
    if (!final) return;
    const duracion = 1200;
    el.textContent = '0';
    setTimeout(() => {
      const inicio = performance.now();
      const paso = (ahora) => {
        const u = Math.min((ahora - inicio) / duracion, 1);
        el.textContent = String(Math.round(final * (1 - Math.pow(1 - u, 3))));
        if (u < 1) requestAnimationFrame(paso);
      };
      requestAnimationFrame(paso);
    }, retraso);
  }

  actualizar() {
    if (!this.lista) return;
    const centro = window.innerHeight * 0.55;
    const caja = this.lista.getBoundingClientRect();
    const progreso = Math.min(Math.max((centro - caja.top) / caja.height, 0), 1);
    this.style.setProperty('--progreso', progreso.toFixed(4));

    let activo = -1;
    this.principios.forEach((principio, i) => {
      const pasado = principio.getBoundingClientRect().top < centro;
      principio.classList.toggle('is-visto', pasado);
      if (pasado) activo = i;
    });
    this.principios.forEach((principio, i) => principio.classList.toggle('is-activo', i === activo));
  }
}

customElements.define('meluva-manifiesto', MeluvaManifiesto);

/**
 * Entrada escalonada genérica: los .meluva-revela__item de dentro suben uno tras otro cuando el
 * bloque entra en pantalla (el orden lo da --orden). Con movimiento reducido no hace nada.
 */
class MeluvaRevela extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.classList.add('is-listo');
    this.observador = new IntersectionObserver(
      ([entrada]) => {
        if (!entrada.isIntersecting) return;
        this.classList.add('is-dentro');
        this.observador.disconnect();
      },
      { threshold: 0.15 }
    );
    this.observador.observe(this);
  }

  disconnectedCallback() {
    this.observador?.disconnect();
  }
}

customElements.define('meluva-revela', MeluvaRevela);

/* La cesta de la cabecera abre la cesta lateral (sin ella, o con Ctrl/Cmd, va a /cart) */
document.addEventListener('click', (evento) => {
  const boton = evento.target.closest('.meluva-boton-cesta');
  const cesta = document.querySelector('meluva-cesta-lateral');
  if (!boton || !cesta || evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.button !== 0) return;
  evento.preventDefault();
  cesta.abrir(boton);
});

/* Ficha de producto: "Añadir" añade sin salir y abre la cesta lateral; "Comprar ya" sigue al pago */
document.addEventListener('submit', async (evento) => {
  const formulario = evento.target.closest('.meluva-ficha__form');
  if (!formulario || evento.submitter?.name === 'return_to' || !document.querySelector('meluva-cesta-lateral')) return;
  evento.preventDefault();

  const boton = evento.submitter;
  boton?.setAttribute('aria-busy', 'true');
  try {
    const cantidad = Math.max(1, parseInt(formulario.elements.quantity?.value, 10) || 1);
    await meluvaAnadirALaCesta(Number(formulario.elements.id.value), cantidad);
  } catch (error) {
    // Si la Cart API falla, el envío normal lleva a la cesta
    formulario.submit();
  } finally {
    boton?.removeAttribute('aria-busy');
  }
});

/* Menú móvil (panel lateral): se cierra con su botón ✕, tocando el fondo oscuro o con
   Escape. El cierre espera a que termine la animación de salida antes de cerrar el
   <details>. Mientras está abierto, la página no se desplaza y el foco no sale del panel. */
const menuSinMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)');

function cerrarMenuMovil(menu, { devolverFoco = false } = {}) {
  if (!menu.open || menu.classList.contains('is-cerrando')) return;

  let respaldo;
  const terminar = () => {
    clearTimeout(respaldo);
    menu.classList.remove('is-cerrando');
    menu.removeAttribute('open');
    if (devolverFoco) menu.querySelector('summary')?.focus();
  };

  const panel = menu.querySelector('.meluva-menu-movil__panel');
  if (menuSinMovimiento.matches || !panel) {
    terminar();
    return;
  }

  menu.classList.add('is-cerrando');
  panel.addEventListener('animationend', function alTerminar(evento) {
    if (evento.target !== panel) return;
    panel.removeEventListener('animationend', alTerminar);
    if (menu.classList.contains('is-cerrando')) terminar();
  });
  // Por si el navegador no llega a lanzar animationend
  respaldo = setTimeout(() => {
    if (menu.classList.contains('is-cerrando')) terminar();
  }, 500);
}

document.querySelectorAll('.meluva-menu-movil').forEach((menu) => {
  menu.addEventListener('toggle', () => {
    document.documentElement.classList.toggle('meluva-sin-scroll', menu.open);
    if (menu.open) menu.querySelector('.meluva-menu-movil__cerrar')?.focus({ preventScroll: true });
  });
});

document.addEventListener('click', (evento) => {
  const boton = evento.target.closest('.meluva-menu-movil > summary');
  if (boton) {
    const menu = boton.parentElement;
    if (menu.classList.contains('is-cerrando')) {
      // Tocar de nuevo mientras se cierra lo vuelve a abrir
      evento.preventDefault();
      menu.classList.remove('is-cerrando');
    } else if (menu.open) {
      evento.preventDefault();
      cerrarMenuMovil(menu);
    }
    return;
  }

  const cerrar = evento.target.closest('.meluva-menu-movil [data-cerrar-menu]');
  if (cerrar) cerrarMenuMovil(cerrar.closest('.meluva-menu-movil'), { devolverFoco: true });
});

document.addEventListener('keydown', (evento) => {
  const menu = document.querySelector('.meluva-menu-movil[open]:not(.is-cerrando)');
  if (!menu) return;
  if (evento.key === 'Escape') {
    cerrarMenuMovil(menu, { devolverFoco: true });
    return;
  }
  if (evento.key !== 'Tab') return;
  const enfocables = [...menu.querySelectorAll('.meluva-menu-movil__panel a[href], .meluva-menu-movil__panel button')];
  const primero = enfocables[0];
  const ultimo = enfocables[enfocables.length - 1];
  if (evento.shiftKey && document.activeElement === primero) {
    evento.preventDefault();
    ultimo.focus();
  } else if (!evento.shiftKey && document.activeElement === ultimo) {
    evento.preventDefault();
    primero.focus();
  }
});

/* Tarjetas de producto: el botón redondo añade a la cesta sin salir de la página */
document.addEventListener('submit', async (evento) => {
  const formulario = evento.target.closest('[data-anadir-rapido]');
  if (!formulario) return;
  evento.preventDefault();

  const boton = formulario.querySelector('.meluva-boton-anadir');
  const estado = formulario.querySelector('[data-anadir-estado]');
  if (boton.getAttribute('aria-busy') === 'true') return;

  boton.setAttribute('aria-busy', 'true');
  try {
    await meluvaAnadirALaCesta(Number(formulario.elements.id.value));
    boton.classList.add('is-anadido');
    if (estado) estado.textContent = 'Añadido a la cesta';
    clearTimeout(boton.meluvaTemporizador);
    boton.meluvaTemporizador = setTimeout(() => {
      boton.classList.remove('is-anadido');
      if (estado) estado.textContent = '';
    }, 1800);
  } catch (error) {
    // Si la Cart API falla, el envío normal lleva a la cesta
    formulario.submit();
  } finally {
    boton.removeAttribute('aria-busy');
  }
});
